import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { SaveProgressStats } from '@/components/settings/save-progress.utils';
import type { PreviewSaveStatus } from '@/components/settings/setting-preview.types';
import type { SettingChange, SettingChangeGroup } from '@/components/settings/settings-save.types';
import { SettingsSaveDialog } from '@/components/settings/settings-save-dialog';

import { type GroupBy, GroupByToggle } from './group-by-toggle';
import {
  discardFormCell,
  groupChangesByEquipment,
  groupChangesBySetting,
} from '../equipment-bulk-edit.helpers';
import type {
  BulkEditChangeMetadata,
  EquipmentBulkEditFormValues,
} from '../equipment-bulk-edit.types';

interface SaveChangesDialogProps {
  hasPendingSave: boolean;
  onCancelPending: () => void;
  onClose: () => void;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  progressStats: SaveProgressStats;
  saveComplete: boolean;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
  settingChanges: SettingChange[];
}

/**
 * SaveChangesDialog
 *
 * Thin wrapper around the shared SettingsSaveDialog that adds bulk-edit-specific
 * behaviour: group-by toggle (equipment vs setting) and cell-level discard via
 * the react-hook-form context.
 */
export const SaveChangesDialog = ({
  hasPendingSave,
  onCancelPending,
  onClose,
  onConfirm,
  onOpenChange,
  open,
  progressStats,
  saveComplete,
  saveProgressByChangeId,
  settingChanges,
}: SaveChangesDialogProps): React.ReactNode => {
  const [groupBy, setGroupBy] = useState<GroupBy>('equipment');

  const {
    setValue,
    formState: { defaultValues },
  } = useFormContext<{ values: EquipmentBulkEditFormValues }>();

  const groups: SettingChangeGroup[] = useMemo(
    () =>
      groupBy === 'equipment'
        ? groupChangesByEquipment(settingChanges)
        : groupChangesBySetting(settingChanges),
    [groupBy, settingChanges]
  );

  // Derive label based on groupBy direction
  const labeledGroups: SettingChangeGroup[] = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        changes: group.changes.map((change) => {
          const meta = change.metadata as unknown as BulkEditChangeMetadata;
          return {
            ...change,
            label: groupBy === 'equipment' ? meta.columnLabel : meta.rowName,
          };
        }),
      })),
    [groups, groupBy]
  );

  const handleDiscard = useCallback(
    (changeId: string) => {
      if (hasPendingSave) return;
      const change = settingChanges.find((c) => c.changeId === changeId);
      if (!change?.metadata) return;
      const { rowId, columnId } = change.metadata as unknown as BulkEditChangeMetadata;
      discardFormCell(setValue, defaultValues, rowId, columnId);
    },
    [settingChanges, defaultValues, hasPendingSave, setValue]
  );

  return (
    <SettingsSaveDialog
      open={open}
      onOpenChange={onOpenChange}
      groups={labeledGroups}
      saveProgressByChangeId={saveProgressByChangeId}
      hasPendingSave={hasPendingSave}
      saveComplete={saveComplete}
      progressStats={progressStats}
      headerContent={<GroupByToggle value={groupBy} onChange={setGroupBy} />}
      onDiscard={handleDiscard}
      onConfirm={onConfirm}
      onCancelPending={onCancelPending}
      onClose={onClose}
    />
  );
};
